import _ from 'lodash';
import PropTypes from 'prop-types';
import React, {ReactNode, useEffect, useRef, useState} from 'react';
import postal from 'postal';
import Label from "./Label";

function createCheckedClass(disabled: boolean, dark: boolean, noTopOffset: boolean, backColor: boolean, isHeader?: boolean) {
  const offsetBaseClass = noTopOffset ? 'e-ifms-checkbox-no-top-offset ' : '';
  const backColorBaseClass = backColor ? 'e-ifms-checkbox-backcolor': '';

  if (disabled) {
    return dark ? `${offsetBaseClass}e-ifms-checkbox-icon-base e-ifms-checkbox-disabled-dark checked` : `${offsetBaseClass}e-ifms-checkbox-icon-base e-ifms-checkbox-disabled checked`;
  } else {
      return dark && isHeader ? `${offsetBaseClass}${backColorBaseClass} e-ifms-checkbox-icon-base e-ifms-checkbox-header-unchecked-dark` : `${offsetBaseClass}${backColorBaseClass} e-ifms-checkbox-icon-base e-ifms-checkbox`;
  }
}

function createUncheckedClass(disabled: boolean, dark: boolean, noTopOffset: boolean, backColor: boolean, isHeader?: boolean) {
  const offsetBaseClass = noTopOffset ? 'e-ifms-checkbox-no-top-offset ' : '';
  const backColorBaseClass = backColor ? 'e-ifms-checkbox-backcolor': '';
  if (disabled) {
    return dark ? `${offsetBaseClass}e-ifms-checkbox-icon-base e-ifms-checkbox-unchecked-disabled-dark unchecked` : `${offsetBaseClass}e-ifms-checkbox-icon-base e-ifms-checkbox-unchecked-disabled unchecked`;
  } else {
    return dark && isHeader ? `${offsetBaseClass} ${backColorBaseClass} e-ifms-checkbox-icon-base e-ifms-checkbox-header-unchecked-dark` : `${offsetBaseClass} ${backColorBaseClass} e-ifms-checkbox-icon-base e-ifms-checkbox-unchecked`;
  }
}

const createChekedCustomStyles = (disabled: boolean, focus: boolean): React.CSSProperties | undefined => !focus ? undefined : {
  border: '1px dotted', width: "14px", height: "14px"
};

const Checked = ({disabled, dark, focus, noTopOffset,backColor, isHeader}: { disabled: boolean, dark: boolean, focus: boolean, noTopOffset: boolean, backColor: boolean, isHeader? : boolean }) => (
  <svg className={createCheckedClass(disabled, dark, noTopOffset, backColor, isHeader)} xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" style={createChekedCustomStyles(disabled, focus)}
       viewBox="0 0 24 24">
    {disabled
      ? <path
        d="M 10.041 17 l -4.5 -4.319 l 2.212 -1.983 l 3.08 2.937 l 7.021 -7.183 l 1.994 2.177 l -8.991 9.111 z m -5.041 -15 c -1.654 0 -3 1.346 -3 3 v 14 c 0 1.654 1.346 3 3 3 h 14 c 1.654 0 3 -1.346 3 -3 v -14 c 0 -1.654 -1.346 -3 -3 -3 h -14 z m 19 3 v 14 c 0 2.761 -2.238 5 -5 5 h -14 c -2.762 0 -5 -2.239 -5 -5 v -14 c 0 -2.761 2.238 -5 5 -5 h 14 c 2.762 0 5 2.239 5 5 z"/>
      : <path
        d="M 10.041 17 l -4.5 -4.319 l 2.212 -1.983 l 3.08 2.937 l 7.021 -7.183 l 1.994 2.177 l -8.991 9.111 z m -5.041 -15 c -1.654 0 -3 1.346 -3 3 v 14 c 0 1.654 1.346 3 3 3 h 14 c 1.654 0 3 -1.346 3 -3 v -14 c 0 -1.654 -1.346 -3 -3 -3 h -14 z m 19 3 v 14 c 0 2.761 -2.238 5 -5 5 h -14 c -2.762 0 -5 -2.239 -5 -5 v -14 c 0 -2.761 2.238 -5 5 -5 h 14 c 2.762 0 5 2.239 5 5 z"/>
    }
  </svg>
);

const createUncheckedCustomStyles = (disabled: boolean, focus: boolean): React.CSSProperties | undefined => !focus ? undefined : {
  border: '1px dotted', width: "14px", height: "14px"
};

const Unchecked = ({disabled, dark, focus, noTopOffset,backColor, isHeader}: { disabled: boolean, dark: boolean, focus: boolean, noTopOffset: boolean, backColor: boolean, isHeader?: boolean }) => (
  <svg className={createUncheckedClass(disabled, dark, noTopOffset, backColor, isHeader)} xmlns="http://www.w3.org/2000/svg" width="100%" height="100%"
       viewBox="0 0 24 24" style={createUncheckedCustomStyles(disabled, focus)}>
    <path
      d="M5 2c-1.654 0-3 1.346-3 3v14c0 1.654 1.346 3 3 3h14c1.654 0 3-1.346 3-3v-14c0-1.654-1.346-3-3-3h-14zm19 3v14c0 2.761-2.238 5-5 5h-14c-2.762 0-5-2.239-5-5v-14c0-2.761 2.238-5 5-5h14c2.762 0 5 2.239 5 5z"/>
  </svg>
);

function CheckBox({
  value,
  onChange = () => {
  },
  disabled = false,
  dark,
  onTabNext = undefined,
  onTabPrevious = undefined,
  focusId = undefined,
  inlist = false,
  reverse = false,
  highlightOnFocus = false,
  skipFocusOnClick = false,
  // Checkboxes with label are positioned to be vertically centered inside <InlineInputControlRow ...>
  // Under some conditions this position causes rendering issue. To avoid it, the following property can be used
  noTopOffset = false,
  label,
  labelColor,
  bold = false,
  backColor = false,
  isHeader = false,
  ...other
}: {
  value: boolean,
  onChange?: (x: boolean) => void,
  disabled?: boolean,
  onTabNext?: (() => boolean) | undefined,
  onTabPrevious?: (() => boolean) | undefined,
  focusId?: string | undefined,
  inlist?: boolean,
  label?: string | ReactNode,
  labelColor?: string,
  reverse?: boolean,
  highlightOnFocus?: boolean,
  skipFocusOnClick?: boolean,
  noTopOffset?: boolean,
  bold?: boolean,
  backColor?: boolean,
  isHeader?: boolean,
  [x: string]: any
}) {
  // const dark = false;

  let notificationSubscription: ISubscriptionDefinition<any> | null = null;
  let toggleSubscription: ISubscriptionDefinition<any> | null = null;

  const [focus, setFocus] = useState(false);
  const [toggleRequestedByParent, setToggleRequestedByParent] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function subscribeChannels() {
    notificationSubscription = postal.subscribe({
      channel: 'ifms',
      topic: 'ifms.parent-click',
      callback: onParentToggle
    });
    toggleSubscription = postal.subscribe({
      channel: 'ifms',
      topic: 'ifms.parent-toggle',
      callback: onParentToggle
    });
    toggleSubscription = postal.subscribe({
      channel: 'ifms',
      topic: 'ifms.checkbox-check',
      callback: (data) => handleClick(data.targetId)
    });
  }

  function unsubscribeChannels() {
    if (notificationSubscription) {
      postal.unsubscribe(notificationSubscription);
    }
    if (toggleSubscription) {
      postal.unsubscribe(toggleSubscription);
    }
  }

  useEffect(() => {
    subscribeChannels();
    return unsubscribeChannels;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const inputId = !!focusId ? focusId : _.uniqueId('ifms-checkbox-');

  function onInnerFocus() {
    if (toggleRequestedByParent) {
      setToggleRequestedByParent(false);
      handleClick();
    }
  }

  function onParentToggle(parentId: string) {
    const myParentId = inputRef.current?.parentElement?.parentElement?.id;

    if (myParentId === parentId) {
      setToggleRequestedByParent(true);
    }
  }

  function handleClick(targetId?: string) {
    if (!inputRef.current || (targetId && targetId !== inputRef.current.id)) return;

    if (!disabled) {
      if (inputRef && inputRef.current && !skipFocusOnClick) {
        inputRef.current.focus();
      }
      onChange(!value);
    }
  }

  function processTabNext(e: React.KeyboardEvent<HTMLInputElement>) {
    if (onTabNext) {
      const tabProcessed = onTabNext();
      if (tabProcessed) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
    }
  }

  function processTabPrevious(e: React.KeyboardEvent<HTMLInputElement>) {
    if (onTabPrevious) {
      const tabProcessed = onTabPrevious();
      if (tabProcessed) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const down = 40;
    const up = 38;
    const left = 37;
    const right = 39;

    if (e.keyCode === up || e.keyCode === down || e.keyCode === left || e.keyCode === right) {
      // const parentId = (e?.target as Element | null)?.parentElement?.id;
      // if (!!parentId) {
      //   channel.publish('ifms.control-navigation', {
      //     parentId,
      //     keyCode: e.keyCode
      //   });
      // }
      // e.preventDefault();
      // e.stopPropagation();
      return;
    }

    const tab = 9;
    const space = 32;

    if (e.keyCode === tab) {
      if (e.shiftKey) {
        processTabPrevious(e);
      } else {
        processTabNext(e);
      }
    } else if (e.keyCode === space) {
      handleClick();
    }
  }

  const containerBase = inlist ? 'e-ifms-checkbox-container e-ifms-checkbox-inlist' : 'e-ifms-checkbox-container';
  let containerClass = `${containerBase}  ifms-tabable ifms-tabable-wrapper`;
  // if (!disabled) {
  //   containerClass = `${containerClass} ifms-tabable-active`;
  // }
  if (highlightOnFocus && focus) {
    containerClass = `${containerClass} ifms-checkbox-focus-highlight`;
  }

  function renderCheckboxWithLabel() {
    if (reverse) {
      return (
        <div style={{display: 'flex', height: '11px'}}>
          <div>
            <div className={noTopOffset ? 'e-ifms-checkbox-no-top-offset' : undefined} onClick={() => { handleClick(); }}><Label id={`${inputId}-lbl`} style={{color: labelColor ? labelColor : 'black', marginRight: 9, cursor: 'pointer', border: focus ? '1px dotted black' : undefined, fontWeight: bold ? 'bold' : 'normal'}} label={label} /></div>
          </div>
          <div
            className={containerClass}
            onClick={() => { handleClick(); }}
            onFocus={() => {
              setFocus(true);
              const focusElement = document.getElementById(inputId);
              if (focusElement && !skipFocusOnClick) {
                focusElement.focus();
              }
            }}
            onBlur={() => {
              setFocus(false);
            }}
            tabIndex={-1}
            {..._.omit(other, ['dark', 'name'])}
          >
            {!disabled &&
              <input
                id={inputId}
                ref={inputRef}
                name={other?.name}
                type="text"
                className={`ifms-checkbox ifms-tabable ${!disabled ? ' ifms-tabable-active' : ''}`}
                style={{width: '16px', height: '16px', position: 'absolute', opacity: '0', cursor: 'pointer'}}
                onKeyDown={onKeyDown}
                value={`${value ? 'true' : 'false'}`}
                onFocus={onInnerFocus}
                onChange={() => {
                }}
              />
            }
            {value ? <Checked noTopOffset={noTopOffset} disabled={disabled} dark={dark} focus={focus} backColor={backColor} isHeader={isHeader} /> :
                <Unchecked noTopOffset={noTopOffset} disabled={disabled} dark={dark} focus={focus} backColor={backColor} isHeader={isHeader} />}
          </div>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', height: '11px' }}>
        <div
          className={containerClass}
          onClick={() => { handleClick(); }}
          onFocus={() => {
            setFocus(true);
            const focusElement = document.getElementById(inputId);
            if (focusElement && !skipFocusOnClick) {
              focusElement.focus();
            }
          }}
          onBlur={() => {
            setFocus(false);
          }}
          tabIndex={-1}
          {..._.omit(other, ['dark', 'name'])}
        >
          {!disabled &&
            <input
              id={inputId}
              ref={inputRef}
              name={other?.name}
              type="text"
              className={`ifms-checkbox ifms-tabable ${!disabled ? ' ifms-tabable-active' : ''}`}
              style={{width: '16px', height: '16px', position: 'absolute', opacity: '0', cursor: 'pointer'}}
              onKeyDown={onKeyDown}
              value={`${value ? 'true' : 'false'}`}
              onFocus={onInnerFocus}
              onChange={() => {
              }}
            />
          }
          {value ? <Checked noTopOffset={noTopOffset} disabled={disabled} dark={dark} focus={focus} backColor={backColor} isHeader={isHeader}/> :
            <Unchecked noTopOffset={noTopOffset} disabled={disabled} dark={dark} focus={focus} backColor={backColor} isHeader={isHeader}/>}
        </div>
          <div className={noTopOffset ? 'e-ifms-checkbox-no-top-offset' : undefined} onClick={() => { handleClick(); }}><Label id={`${inputId}-lbl`} style={{color: labelColor ? labelColor : 'black', marginLeft: 9, cursor: 'pointer', border: focus ? '1px dotted black' : undefined, fontWeight: bold ? 'bold' : 'normal'}} label={label} /></div>
      </div>
    );
  }

  // Be careful when changing the implemenation.
  //  Other controls like SelectableOverlay are tightly coupled with the implementation to be able to provide custom behaviour
  return (
    !!label ? (
      renderCheckboxWithLabel()
    ) : (
      <div
        className={containerClass}
        onClick={() => { handleClick(); }}
        onFocus={() => {
          setFocus(true);
          const focusElement = document.getElementById(inputId);
          if (focusElement && !skipFocusOnClick) {
            focusElement.focus();
          }
        }}
        onBlur={() => {
          setFocus(false);
        }}
        tabIndex={-1}
        {..._.omit(other, ['dark', 'name'])}
      >
        {!disabled &&
          <input
            id={inputId}
            ref={inputRef}
            name={other?.name}
            type="text"
            className={`ifms-checkbox ifms-tabable ${!disabled ? ' ifms-tabable-active' : ''}`}
            style={{width: '1px', height: '1px', position: 'absolute', opacity: '0', cursor: 'pointer'}}
            onKeyDown={onKeyDown}
            value={`${value ? 'true' : 'false'}`}
            onFocus={onInnerFocus}
            onChange={() => {
            }}
          />
        }
        {value ? <Checked noTopOffset={noTopOffset} disabled={disabled} dark={dark} focus={focus} backColor={backColor} isHeader={isHeader}/> :
          <Unchecked noTopOffset={noTopOffset} disabled={disabled} dark={dark} focus={focus} backColor={backColor} isHeader={isHeader}/>}
      </div>
    )
 );
}
CheckBox.defaultProps = {
  onChange: () => {
  },
  value: false,
  disabled: false,
  dark: false,
  inlist: false,
  reverse: false,
  highlightOnFocus: false,
  skipFocusOnClick: false,
  noTopOffset: false,
  bold: false,
  backColor: false,
  isHeader: false,
};

CheckBox.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  dark: PropTypes.bool.isRequired,
  onTabNext: PropTypes.func,
  onTabPrevious: PropTypes.func,
  focusId: PropTypes.string,
  inlist: PropTypes.bool.isRequired,
  label: PropTypes.any,
  reverse: PropTypes.bool.isRequired,
  highlightOnFocus: PropTypes.bool.isRequired,
  skipFocusOnClick: PropTypes.bool.isRequired,
  noTopOffset: PropTypes.bool.isRequired,
  bold: PropTypes.bool.isRequired,
  backColor: PropTypes.bool.isRequired,
  isHeader: PropTypes.bool,
};


export default React.memo(CheckBox);
